import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { BsImage, BsTrash, BsUpload } from "react-icons/bs";
import { getJwtToken, END_POINT } from "../../Redux/AdminReducer/action";

const SPECIALITY_URL = `${END_POINT}/clinic-profile/website-speciality`;

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || (typeof error?.response?.data === "string" ? error.response.data : "") || fallback;

// "Website — Speciality section image" card on the profile page. Drives the
// "Speciality of Dr. Shital's Line of Treatment" section on the public website.
const WebsiteSpecialityCard = () => {
  const [data, setData] = useState({ image: null, title: null, text: null });
  const [text, setText] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef(null);

  const applyData = (next) => {
    const value = next || { image: null, title: null, text: null };
    setData(value);
    setText(value.text || "");
  };

  useEffect(() => {
    let active = true;
    axios
      .get(SPECIALITY_URL, { headers: { Authorization: getJwtToken() } })
      .then((res) => active && applyData(res?.data?.data))
      .catch((error) => {
        console.error(error);
        if (active) toast.error(errorMessage(error, "Could not load the website speciality section"));
      })
      .finally(() => active && setIsLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const save = async (formData, successMessage) => {
    setIsSaving(true);
    try {
      const res = await axios.post(SPECIALITY_URL, formData, {
        headers: { Authorization: getJwtToken() },
      });
      applyData(res?.data?.data);
      toast.success(successMessage);
      return true;
    } catch (error) {
      console.error(error);
      toast.error(errorMessage(error, "Could not save the website speciality section"));
      return false;
    } finally {
      setIsSaving(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/") || file.type === "image/svg+xml") {
      toast.error("Please choose a JPG, PNG, GIF, WEBP or BMP image");
      event.target.value = "";
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be 8 MB or smaller");
      event.target.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("image", file);
    save(fd, "Speciality image updated");
  };

  const handleRemove = () => {
    if (!window.confirm("Remove the speciality image? The website will show its default image.")) return;
    const fd = new FormData();
    fd.append("removeImage", "true");
    save(fd, "Speciality image removed");
  };

  const handleSaveText = () => {
    const fd = new FormData();
    fd.append("text", text);
    save(fd, "Speciality text saved");
  };

  const textChanged = (text || "").trim() !== (data.text || "");

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 md:p-6 mt-4">
      <div className="border-b border-slate-100 pb-3 mb-4">
        <h3 className="text-base md:text-lg font-semibold text-slate-800">Website — Speciality section image</h3>
        <p className="text-xs text-slate-500">
          Shown in the "Speciality of Dr. Shital's Line of Treatment" section of the website. Leave empty to use the default.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row items-start gap-4 p-3.5 rounded-xl border border-slate-200 bg-slate-50/70">
        {data.image ? (
          <div className="w-40 h-40 p-1.5 bg-white border border-slate-200 rounded-lg flex items-center justify-center flex-shrink-0 shadow-xs">
            <img src={data.image} alt="Speciality section" className="max-h-full max-w-full object-contain rounded" />
          </div>
        ) : (
          <div className="w-40 h-40 bg-slate-100 border border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center text-slate-400 flex-shrink-0">
            <BsImage className="w-7 h-7 mb-1 text-slate-400" />
            <span className="text-[11px]">{isLoading ? "Loading..." : "Default image"}</span>
          </div>
        )}

        <div className="flex-1 w-full min-w-0 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp,image/bmp"
              className="hidden"
              onChange={handleFileChange}
            />
            <button
              type="button"
              disabled={isSaving || isLoading}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-medium text-xs md:text-sm py-2 px-4 rounded-lg shadow-sm transition disabled:opacity-50 cursor-pointer"
            >
              <BsUpload className="w-3.5 h-3.5" />
              <span>{data.image ? "Replace image" : "Upload image"}</span>
            </button>
            {data.image && (
              <button
                type="button"
                disabled={isSaving}
                onClick={handleRemove}
                className="inline-flex items-center gap-2 bg-white hover:bg-red-50 text-red-600 border border-red-200 font-medium text-xs md:text-sm py-2 px-4 rounded-lg shadow-sm transition disabled:opacity-50 cursor-pointer"
              >
                <BsTrash className="w-3.5 h-3.5" />
                <span>Remove</span>
              </button>
            )}
            {isSaving && <span className="text-[11px] text-slate-500">Saving...</span>}
          </div>
          <p className="text-[11px] text-slate-400">JPG, PNG, GIF, WEBP or BMP, up to 8 MB. A portrait photo works best.</p>

          <div>
            <label className="block mb-1 text-xs md:text-sm font-medium text-gray-700">Section text (optional)</label>
            <textarea
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={isLoading}
              placeholder="Leave empty to keep the website's default text"
              className="w-full text-sm text-slate-700 border border-slate-200 rounded-lg p-2 bg-white focus:outline-none focus:ring-2 focus:ring-sky-200"
            />
            <button
              type="button"
              disabled={isSaving || isLoading || !textChanged}
              onClick={handleSaveText}
              className="mt-2 inline-flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 text-white font-medium text-xs md:text-sm py-2 px-4 rounded-lg shadow-sm transition disabled:opacity-50 cursor-pointer"
            >
              Save text
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebsiteSpecialityCard;
